import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type { Friend, FriendRequestOutcome } from '../../shared/api';
import { Avatar } from '../components/Avatar';
import { useResource } from '../hooks/useResource';
import { api, errorMessage } from '../lib/api';
import { formatDate } from '../lib/format';

const OUTCOME_MESSAGES: Record<FriendRequestOutcome, (email: string) => string> = {
  sent: (email) => `Request sent to ${email}. They'll see it next time they sign in.`,
  already_sent: (email) => `You've already sent ${email} a request.`,
  accepted: (email) => `${email} had already asked you, so you're now friends.`,
  already_friends: (email) => `You're already friends with ${email}.`,
};

export function FriendsPage() {
  const { data, error, reload } = useResource('friends', api.listFriends);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Runs a button's action with the buttons disabled, then reloads the lists. */
  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      reload();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function removeFriend(friend: Friend) {
    const name = friend.name ?? friend.email;
    if (!window.confirm(`Remove ${name} as a friend? You'll no longer see each other's collections.`)) return;
    void act(() => api.removeFriend(friend.id));
  }

  if (!data) {
    return error ? (
      <div className="page-status" role="alert">
        <p className="error-text">{errorMessage(error)}</p>
        <button type="button" className="btn btn--primary" onClick={reload}>
          Try again
        </button>
      </div>
    ) : (
      <p className="page-status">Loading friends…</p>
    );
  }

  const { friends, incoming, outgoing } = data;

  return (
    <section className="friends-page">
      <h1>Friends</h1>
      <p className="muted">
        Friends can see each other's collections, with the built-in categories such as Wishlist and Read. Your own categories stay
        private. Someone you live with belongs in your <Link to="/household">household</Link> instead, where you share one collection.
      </p>

      <AddFriendForm onSent={reload} />

      {actionError && (
        <p className="error-text" role="alert">
          {actionError}
        </p>
      )}

      {incoming.length > 0 && (
        <>
          <h2>Requests for you</h2>
          <ul className="friend-list">
            {incoming.map((request) => (
              <li key={request.id} className="friend-list__item">
                <Avatar user={request.from} className="friend-avatar" />
                <span className="friend-list__who">
                  <span className="friend-list__name">{request.from.name ?? request.from.email}</span>
                  <span className="muted">{request.from.email}</span>
                </span>
                <span className="friend-list__actions">
                  <button
                    type="button"
                    className="btn btn--primary btn--small"
                    disabled={busy}
                    onClick={() => void act(() => api.acceptFriendRequest(request.id))}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    disabled={busy}
                    onClick={() => void act(() => api.deleteFriendRequest(request.id))}
                  >
                    Decline
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <h2>Your friends</h2>
      {friends.length === 0 ? (
        <p className="hint">No friends yet. Add someone by the email address they use to sign in.</p>
      ) : (
        <ul className="friend-list">
          {friends.map((friend) => (
            <li key={friend.id} className="friend-list__item">
              <Avatar user={friend} className="friend-avatar" />
              <Link className="friend-list__who" to={`/friends/${friend.id}`}>
                <span className="friend-list__name">{friend.name ?? friend.email}</span>
                <span className="muted">
                  {friend.bookCount} {friend.bookCount === 1 ? 'book' : 'books'}
                </span>
              </Link>
              <span className="friend-list__actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--small btn--danger-text"
                  disabled={busy}
                  onClick={() => removeFriend(friend)}
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {outgoing.length > 0 && (
        <>
          <h2>Waiting for an answer</h2>
          <ul className="friend-list">
            {outgoing.map((request) => (
              <li key={request.id} className="friend-list__item">
                <span className="friend-list__who">
                  <span className="friend-list__name">{request.email}</span>
                  <span className="muted">Sent {formatDate(request.createdAt)}</span>
                </span>
                <span className="friend-list__actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    disabled={busy}
                    onClick={() => void act(() => api.deleteFriendRequest(request.id))}
                  >
                    Cancel
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function AddFriendForm({ onSent }: { onSent: () => void }) {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const outcome = await api.sendFriendRequest(address);
      setMessage(OUTCOME_MESSAGES[outcome](address));
      setEmail('');
      onSent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="new-category" onSubmit={(event) => void handleSubmit(event)}>
      <label htmlFor={inputId} className="visually-hidden">
        Friend's email address
      </label>
      <div className="new-category__row">
        <input
          id={inputId}
          type="email"
          placeholder="Friend's email address"
          autoComplete="off"
          inputMode="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          aria-invalid={error !== null}
        />
        <button type="submit" className="btn btn--primary btn--small" disabled={saving || !email.trim()}>
          {saving ? 'Sending…' : 'Add friend'}
        </button>
      </div>
      {message && (
        <p className="success-text" role="status">
          {message}
        </p>
      )}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
